import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api'; // Import API_ENDPOINTS
import './ProfilePage.css';
import { useNavigate } from 'react-router-dom';
import { ProgressiveImage } from './ProgressiveImage';
import { Post as PostData, Photo as PhotoData } from './HomePage'; // Import the richer Post type and Photo type from HomePage
import { User as FirebaseUser } from "firebase/auth"; // Import Firebase User type
import PostModal from './PostModal'; // Add PostModal import
import { DEFAULT_PROFILE_PICTURE } from '../constants/defaults';
import PostCard from './PostCard'; // Import the shared PostCard
import ReauthModal from './ReauthModal'; // Import the new modal
import FollowListModal from './FollowListModal'; // Import the modal
import { List } from '../types/list';

// Define the structure for the user's own posts (simpler than HomePage Post)
interface UserPost {
  id: string;
  title: string;
  content: string;
  created_at: string;
  visit_date?: string;
  first_photo?: PhotoData;
  restaurant_name?: string;
  image_urls: string; // Assuming this field exists based on original code
  // User posts might not have the structured first_photo; handled in card
}

type ActiveTab = 'myPosts' | 'myPostsYums' | 'lists';

// Reusable Post Card Component (similar to HomePage but without actions or simplified)
const ProfilePostCard: React.FC<{
  post: PostData | UserPost;
  isYummedPost?: boolean;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onClick?: () => void;
}> = ({ post, isYummedPost = false, onEdit, onDelete, onClick }) => {
  // Determine image source - use first_photo if available (from yummed posts or potentially user posts)
  // Fallback to image_urls if that's what user posts provide
  let image: PhotoData | { thumbnailUrl: string; displayUrl: string } | undefined;
  if ('first_photo' in post && post.first_photo) {
    // For yummed posts, construct proper URLs
    image = {
      ...post.first_photo,
      thumbnailUrl: `${API_BASE_URL}/api/posts/images/${post.first_photo.thumbnailUrl}`,
      displayUrl: `${API_BASE_URL}/api/posts/images/${post.first_photo.displayUrl}`
    };
  } else if ('image_urls' in post && post.image_urls) {
    const firstImageUrl = post.image_urls.split(',')[0];
    if (firstImageUrl) {
      image = { 
        thumbnailUrl: `${API_BASE_URL}/api/posts/images/${firstImageUrl}`,
        displayUrl: `${API_BASE_URL}/api/posts/images/${firstImageUrl}`
      };
    }
  }

  // Safely get the image name if available
  const imageName = (image && 'name' in image) ? image.name : undefined;

  return (
    <div 
      className={`post-card profile-post-card ${isYummedPost ? 'clickable' : ''}`}
      onClick={isYummedPost ? onClick : undefined}
    >
      <div className="post-header">
        <div className="post-info">
          <span className="post-title">{post.title}</span>
          <span className="post-date">
            {new Date(post.visit_date || post.created_at).toLocaleDateString()}
          </span>
          {/* Optionally show author if it's a yummed post */}
          {isYummedPost && (post as PostData).user_display_name && (
            <span className="post-author">by {(post as PostData).user_display_name}</span>
          )}
        </div>
        {/* Only show Edit/Delete for user's own posts */}
        {!isYummedPost && onEdit && onDelete && (
          <div className="post-actions">
            <button onClick={() => onEdit(post.id)} className="edit-button" title="Edit post">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" /></svg>
            </button>
            <button onClick={() => onDelete(post.id)} className="delete-button" title="Delete post">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
            </button>
          </div>
        )}
      </div>
      {image && (
        <div className="post-image-container">
          <ProgressiveImage
            src={image.displayUrl}
            thumbnailSrc={image.thumbnailUrl}
            alt={imageName || post.title || 'Post image'}
            className="post-image"
          />
        </div>
      )}
      <div className="post-content">
        <p className="post-text">{post.content}</p>
      </div>
    </div>
  );
};

// Update UserProfile interface to include follow counts
interface UserProfile {
  id: string;
  username: string;
  email: string;
  created_at: string;
  metadata: { 
    display_name?: string;
    firebase_info?: any; 
  };
  followerCount: number; // Added from backend
  followingCount: number; // Added from backend
}

// Add interface for user returned by follow list endpoints
interface FollowerUser {
  id: string;
  username: string;
  metadata?: { 
    display_name?: string;
    firebase_info?: { picture?: string };
  };
}

// Add this interface at the top of the file
interface ListUser {
  id: string;
  username: string;
  user_display_name: string;
  picture: string;
}

// Add this helper function to convert FollowerUser to ListUser
const convertToListUser = (user: FollowerUser): ListUser => ({
  id: user.id,
  username: user.username,
  user_display_name: user.metadata?.display_name || user.username,
  picture: user.metadata?.firebase_info?.picture || DEFAULT_PROFILE_PICTURE
});

const LoadingSkeleton: React.FC = () => (
  <div className="profile-container">
    <div className="profile-header">
      <div className="skeleton skeleton-avatar" />
      <div className="skeleton skeleton-text" style={{ width: '200px', margin: '0 auto' }} />
      <div className="skeleton skeleton-text" style={{ width: '150px', margin: '0 auto' }} />
    </div>
    <div className="profile-stats">
      {[...Array(3)].map((_, index) => (
        <div key={index} className="stat-item">
          <div className="skeleton skeleton-text" style={{ width: '50px', margin: '0 auto' }} />
          <div className="skeleton skeleton-text" style={{ width: '80px', margin: '0.5rem auto 0' }} />
        </div>
      ))}
    </div>
    <div className="profile-content">
      <div className="posts-grid">
        {[...Array(3)].map((_, index) => (
          <div key={index} className="skeleton skeleton-card" />
        ))}
      </div>
    </div>
  </div>
);

export default function ProfilePage() {
  const { currentUser, logout } = useAuth();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userPosts, setUserPosts] = useState<UserPost[]>([]);
  const [yummedPosts, setYummedPosts] = useState<PostData[]>([]);
  const [activeTab, setActiveTab] = useState<ActiveTab>('myPosts');
  const [loading, setLoading] = useState({ profile: true, myPosts: true, myPostsYums: true, followList: false });
  const [error, setError] = useState('');
  const [isEditingDisplayName, setIsEditingDisplayName] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState('');
  const [selectedPost, setSelectedPost] = useState<PostData | null>(null);
  const navigate = useNavigate();
  const [loadingPostDetails, setLoadingPostDetails] = useState(false);
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // --- State for Follow List Modal ---
  const [isFollowModalOpen, setIsFollowModalOpen] = useState(false);
  const [followListTitle, setFollowListTitle] = useState('');
  const [followListUsers, setFollowListUsers] = useState<FollowerUser[] | null>(null);
  const [followListError, setFollowListError] = useState<string | null>(null);
  // --- End Follow List Modal State ---

  const [lists, setLists] = useState<List[]>([]);
  const [loadingLists, setLoadingLists] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [isCreatingList, setIsCreatingList] = useState(false);
  const [listCreationError, setListCreationError] = useState<string | null>(null);

  // fetchUserProfile already fetches the profile which now includes counts
  const fetchUserProfile = useCallback(async (user: FirebaseUser) => {
    setLoading(prev => ({ ...prev, profile: true }));
    try {
        const token = await user.getIdToken();
        const response = await fetch(`${API_BASE_URL}/api/users/profile`, {
            headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) {
            if (response.status === 404) {
                console.warn('Profile not found, sync might be needed.');
                setError('Profile data not found. Please try reloading.');
            } else {
                throw new Error('Failed to fetch user profile');
            }
            setUserProfile(null); 
        } else {
            const data: UserProfile = await response.json(); // Data includes follower/following counts
            
            // Safely parse metadata (as before)
            let metadata = {};
            if (typeof data.metadata === 'string') {
                try { metadata = JSON.parse(data.metadata); } catch (e) { console.error("Failed to parse metadata:", e); metadata = {}; }
            } else if (typeof data.metadata === 'object' && data.metadata !== null) {
                metadata = data.metadata; 
            }
            data.metadata = metadata; 
            
            setUserProfile(data); // Set the profile data including counts
            setNewDisplayName(data.metadata?.display_name || 'User');
            console.log("Fetched profile with follow counts:", data);
        }
    } catch (err) {
        setError('Failed to load profile data');
        console.error('Error fetching user profile:', err);
        setUserProfile(null);
    } finally {
        setLoading(prev => ({ ...prev, profile: false }));
    }
  }, []);

  const fetchUserPosts = useCallback(async () => {
    if (!currentUser) return;
    setLoading(prev => ({ ...prev, myPosts: true }));
    setError('');
    try {
      const token = await currentUser.getIdToken();
      // Ensure the endpoint fetches necessary fields, potentially including first_photo
      const response = await fetch(`${API_BASE_URL}/api/posts/user/${currentUser.uid}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch user posts');
      const data = await response.json();
      setUserPosts(data);
    } catch (err) {
      setError('Failed to load your posts');
      console.error('Error fetching user posts:', err);
    } finally {
      setLoading(prev => ({ ...prev, myPosts: false }));
    }
  }, [currentUser]);

  const fetchYummedPosts = useCallback(async () => {
    if (!currentUser) return;
    setLoading(prev => ({ ...prev, myPostsYums: true }));
    setError(''); // Reset error for this tab
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/users/me/yums`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch yummed posts');
      const data = await response.json();
      setYummedPosts(data);
    } catch (err) {
      setError('Failed to load yummed posts');
      console.error('Error fetching yummed posts:', err);
    } finally {
      setLoading(prev => ({ ...prev, myPostsYums: false }));
    }
  }, [currentUser]);

  const fetchLists = useCallback(async () => {
    if (!currentUser) return;
    setLoadingLists(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/lists`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch lists');
      const data = await response.json();
      setLists(data);
    } catch (err) {
      console.error('Error fetching lists:', err);
    } finally {
      setLoadingLists(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      fetchUserProfile(currentUser); // Fetch profile data
      fetchUserPosts();
      fetchYummedPosts();
      fetchLists();
    }
  }, [currentUser, fetchUserProfile, fetchUserPosts, fetchYummedPosts, fetchLists]);

  const handleUpdateDisplayName = async () => {
      if (!currentUser || !userProfile || !newDisplayName.trim()) return;

      const originalDisplayName = userProfile.metadata?.display_name || userProfile.username;
      if (newDisplayName.trim() === originalDisplayName) {
          setIsEditingDisplayName(false); // No change, just exit edit mode
          return;
      }

      try {
          const token = await currentUser.getIdToken();
          const response = await fetch(`${API_BASE_URL}/api/users/profile`, {
              method: 'PUT',
              headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
              },
              body: JSON.stringify({ display_name: newDisplayName.trim() }),
          });

          if (!response.ok) {
              throw new Error('Failed to update display name');
          }

          const updatedProfileData: UserProfile = await response.json();
          // Reparse metadata just in case
          let metadata = {};
          if (typeof updatedProfileData.metadata === 'string') {
              try {
                  metadata = JSON.parse(updatedProfileData.metadata);
              } catch (e) { metadata = {}; }
          } else if (typeof updatedProfileData.metadata === 'object' && updatedProfileData.metadata !== null) {
              metadata = updatedProfileData.metadata;
          }
          updatedProfileData.metadata = metadata;

          setUserProfile(updatedProfileData); // Update profile state with new data
          setIsEditingDisplayName(false); // Exit edit mode
          setError(''); // Clear any previous errors

      } catch (err) {
          setError('Failed to update display name. Please try again.');
          console.error('Error updating display name:', err);
          // Optionally revert input field to original name on error
          setNewDisplayName(originalDisplayName || '');
      }
  };

  const handleDeletePost = async (postId: string) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    if (!currentUser) return;

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_ENDPOINTS.POSTS}/${postId}`, { // Use API_ENDPOINTS
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to delete post');
      setUserPosts(prev => prev.filter(post => post.id !== postId)); // Update userPosts state
    } catch (err) {
      setError('Failed to delete post');
      console.error('Error deleting post:', err);
    }
  };

  const handleEditPost = (postId: string) => {
    navigate(`/edit-post/${postId}`);
  };

  // Function to fetch full details for a single post (like in HomePage)
  const fetchAndShowPostDetails = async (postId: string) => {
    setLoadingPostDetails(true);
    try {
      const headers: HeadersInit = {};
      if (currentUser) {
        const token = await currentUser.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_ENDPOINTS.POSTS}/${postId}`, {
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch post details (${response.status})`);
      }

      const fullPostData: PostData = await response.json();

      // Ensure photos array exists
      if (!fullPostData.photos) {
        fullPostData.photos = [];
      }

      // Update the selected post state only if the modal is still open for this post
      setSelectedPost(currentSelectedPost => {
        if (currentSelectedPost && currentSelectedPost.id === postId) {
          // Merge fetched data, but keep local yum state if potentially different?
          // For now, let's assume backend is the source of truth after fetch.
          return { ...currentSelectedPost, ...fullPostData };
        }
        return currentSelectedPost; // No change if modal closed or different post selected
      });

    } catch (err) {
      console.error('Error fetching full post details:', err);
      setError(err instanceof Error ? err.message : 'Could not load post details');
      // Optionally close the modal or show error within it
    } finally {
      setLoadingPostDetails(false);
    }
  };

  const handlePostClick = (post: PostData) => {
    if (activeTab === 'myPostsYums') {
      setSelectedPost(post); // Show modal immediately with basic data
      fetchAndShowPostDetails(post.id); // Fetch full details
    }
  };

  const handleCloseModal = () => {
    setSelectedPost(null);
  };

  // Function to call the backend API for deletion (called after re-auth)
  const performAccountDeletion = async () => {
    if (!currentUser) return;

    setIsDeleting(true);
    setDeleteError(null); // Clear previous deletion errors

    try {
      // Force refresh to get the latest token after re-authentication
      const token = await currentUser.getIdToken(true); 
      
      const response = await fetch(`${API_BASE_URL}/api/users/me`, { 
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        let errorMsg = 'Failed to delete account. Please try again.';
        try {
          const errorData = await response.json();
          if (errorData && errorData.message) {
            errorMsg = errorData.message;
          } else if (errorData && errorData.details) {
            errorMsg = errorData.details; // Use details if message isn't present
          }
        } catch (parseError) {
          console.error("Failed to parse error response:", parseError);
        }
        throw new Error(errorMsg);
      }

      console.log('Account deleted successfully on backend.');
      setShowReauthModal(false); // Close modal on success
      await logout(); 
      navigate('/'); 

    } catch (err: any) {
      console.error('Error deleting account via API:', err);
      // Set error state to display message (potentially in the modal or on the page)
      setDeleteError(err.message || 'An unknown error occurred during account deletion.');
       // Keep the modal open on error so the user sees the message
    } finally {
      setIsDeleting(false);
    }
  };

  // Initial handler when delete button is clicked
  const handleDeleteAccountClick = async () => {
    if (!currentUser) return;

    const confirmationMessage =
      `Are you absolutely sure you want to delete your account?\nThis will permanently delete your profile, all your posts, and your yums.\nThis action cannot be undone.`;

    if (!window.confirm(confirmationMessage)) {
      return;
    }
    
    setDeleteError(null); // Clear previous errors before opening modal
    // Open the re-authentication modal
    setShowReauthModal(true);
  };

  // --- Show Lists Handlers ---
  const fetchFollowList = async (type: 'followers' | 'following') => {
    if (!userProfile) return;

    setIsFollowModalOpen(true);
    setFollowListTitle(type === 'followers' ? 'Followers' : 'Following');
    setFollowListUsers(null); // Clear previous list
    setFollowListError(null);
    setLoading(prev => ({ ...prev, followList: true }));

    try {
        const token = await currentUser?.getIdToken();
        if (!token) throw new Error("Not authenticated");

        const endpoint = type === 'followers' 
            ? API_ENDPOINTS.getUserFollowers(userProfile.id)
            : API_ENDPOINTS.getUserFollowing(userProfile.id);

        const response = await fetch(endpoint, {
            headers: { 'Authorization': `Bearer ${token}` },
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: `Failed to fetch ${type}` }));
            throw new Error(errorData.error || `Failed to fetch ${type}`);
        }

        const data: FollowerUser[] = await response.json();
        setFollowListUsers(data);

    } catch (err) {
        console.error(`Error fetching ${type}:`, err);
        setFollowListError(err instanceof Error ? err.message : `Could not load ${type}`);
        setFollowListUsers([]); // Set empty array on error to stop loading
    } finally {
        setLoading(prev => ({ ...prev, followList: false }));
    }
  };

  const handleShowFollowers = () => {
      fetchFollowList('followers');
  };
  
  const handleShowFollowing = () => {
      fetchFollowList('following');
  };

  const closeFollowListModal = () => {
      setIsFollowModalOpen(false);
      setFollowListUsers(null);
      setFollowListError(null);
  };
  // --- End Show Lists Handlers ---

  const handleListClick = (listId: number) => {
    navigate(`/lists/${listId}`);
  };

  const handleCreateList = async () => {
    if (!currentUser || !newListName.trim()) return;
    
    setIsCreatingList(true);
    setListCreationError(null);
    
    try {
        const token = await currentUser.getIdToken();
        const response = await fetch(`${API_BASE_URL}/api/lists`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                name: newListName.trim()
            }),
        });

        if (response.status === 400) {
            throw new Error('List name is required');
        } else if (response.status === 401) {
            throw new Error('You must be logged in to create a list');
        } else if (!response.ok) {
            throw new Error('Failed to create list');
        }

        const newList = await response.json();
        setNewListName(''); // Reset input field
        navigate(`/lists/${newList.id}`); // Navigate to the new list page
    } catch (err) {
        setListCreationError(err instanceof Error ? err.message : 'Failed to create list');
        console.error('Error creating list:', err);
    } finally {
        setIsCreatingList(false);
    }
  };

  const handleDeleteList = async (listId: number) => {
    if (!currentUser) return;
    
    if (!window.confirm('Are you sure you want to delete this list? This action cannot be undone.')) {
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/lists/${listId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to delete list');
      
      setLists(prevLists => prevLists.filter(list => list.id !== listId));
    } catch (err) {
      setError('Failed to delete list. Please try again.');
      console.error('Error deleting list:', err);
    }
  };

  const handleToggleVisibility = async (listId: number, currentVisibility: string) => {
    if (!currentUser) return;

    try {
      const token = await currentUser.getIdToken();
      const newVisibility = currentVisibility === 'private' ? 'public' : 'private';
      
      const response = await fetch(`${API_BASE_URL}/api/lists/${listId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ visibility: newVisibility }),
      });

      if (!response.ok) throw new Error('Failed to update list visibility');
      
      setLists(prevLists => 
        prevLists.map(list => 
          list.id === listId 
            ? { ...list, visibility: newVisibility }
            : list
        )
      );
    } catch (err) {
      setError('Failed to update list visibility. Please try again.');
      console.error('Error updating list visibility:', err);
    }
  };

  if (!currentUser) {
    return null;
  }

  if (loading.profile || (activeTab === 'myPosts' && loading.myPosts) || (activeTab === 'myPostsYums' && loading.myPostsYums)) {
    return (
      <div className="profile-page">
        <div className="profile-container">
          <div className="profile-header">
            <div className="skeleton skeleton-avatar" />
            <div className="profile-info">
              <div className="skeleton skeleton-text" style={{ width: '200px' }} />
              <div className="skeleton skeleton-text" style={{ width: '150px' }} />
            </div>
          </div>
          <div className="profile-stats">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="stat-item">
                <div className="skeleton skeleton-text" style={{ width: '50px' }} />
                <div className="skeleton skeleton-text" style={{ width: '80px' }} />
              </div>
            ))}
          </div>
          <div className="profile-content">
            <div className="profile-tabs">
              <div className="skeleton skeleton-text" style={{ width: '100px' }} />
              <div className="skeleton skeleton-text" style={{ width: '100px' }} />
            </div>
            <div className="posts-grid">
              {[...Array(3)].map((_, index) => (
                <div key={index} className="skeleton skeleton-card" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!userProfile && !loading.profile) {
    return (
      <div className="profile-page">
        <div className="profile-container">
          <div className="error-message">
            <svg className="error-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p>{error || 'Could not load profile data. Please try refreshing the page.'}</p>
          </div>
        </div>
      </div>
    );
  }

  const displayUserName = userProfile?.metadata?.display_name || userProfile?.username || 'User';
  const avatarUrl = userProfile?.metadata?.firebase_info?.picture || DEFAULT_PROFILE_PICTURE;

  const providerId = currentUser?.providerData?.[0]?.providerId;

  const postsToDisplay = activeTab === 'myPosts' ? userPosts : yummedPosts;
  const isLoadingCurrentTab = activeTab === 'myPosts' ? loading.myPosts : loading.myPostsYums;

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-header">
          <img src={avatarUrl} alt="Profile" className="profile-avatar" />
          <div className="profile-info">
            <div className="profile-name-follow-container">
              <h1>{displayUserName}</h1>
              {isEditingDisplayName ? (
                <div className="display-name-edit">
                  <input
                    type="text"
                    value={newDisplayName}
                    onChange={(e) => setNewDisplayName(e.target.value)}
                    className="display-name-input"
                    placeholder="Enter display name"
                  />
                  <button onClick={handleUpdateDisplayName} className="save-button">Save</button>
                  <button onClick={() => setIsEditingDisplayName(false)} className="cancel-button">Cancel</button>
                </div>
              ) : (
                <button
                  onClick={() => setIsEditingDisplayName(true)}
                  className="edit-name-button"
                  title="Edit display name"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                </button>
              )}
            </div>
            {userProfile && <span className="username-display">@{userProfile.username}</span>}
          </div>
          <div className="profile-stats">
            <div className="stat-item">
              <span className="stat-number">{userPosts.length}</span>
              <span className="stat-label">Posts</span>
            </div>
            <div className="stat-item clickable" onClick={handleShowFollowers}>
              <span className="stat-number">{userProfile?.followerCount || 0}</span>
              <span className="stat-label">Followers</span>
            </div>
            <div className="stat-item clickable" onClick={handleShowFollowing}>
              <span className="stat-number">{userProfile?.followingCount || 0}</span>
              <span className="stat-label">Following</span>
            </div>
          </div>
        </div>

        <div className="profile-content">
          <div className="profile-tabs">
            <button
              className={`tab-button ${activeTab === 'myPosts' ? 'active' : ''}`}
              onClick={() => setActiveTab('myPosts')}
            >
              My Posts
            </button>
            <button
              className={`tab-button ${activeTab === 'myPostsYums' ? 'active' : ''}`}
              onClick={() => setActiveTab('myPostsYums')}
            >
              My Yums
            </button>
            <button
              className={`tab-button ${activeTab === 'lists' ? 'active' : ''}`}
              onClick={() => setActiveTab('lists')}
            >
              My Lists
            </button>
          </div>

          {error && (
            <div className="error-message">
              <svg className="error-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p>{error}</p>
            </div>
          )}

          {activeTab === 'lists' ? (
            <div className="lists-container">
              <div className="create-list-section">
                <input
                  type="text"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder="Enter list name"
                  className="list-name-input"
                  disabled={isCreatingList}
                />
                <button
                  className="create-list-button"
                  onClick={handleCreateList}
                  disabled={!newListName.trim() || isCreatingList}
                >
                  {isCreatingList ? 'Creating...' : 'Create List'}
                </button>
                {listCreationError && <div className="list-error">{listCreationError}</div>}
              </div>

              {loadingLists ? (
                <div className="loading">Loading lists...</div>
              ) : lists.length === 0 ? (
                <div className="no-lists">
                  <p>You haven't created any lists yet.</p>
                </div>
              ) : (
                <div className="lists-grid">
                  {lists.map(list => {
                    const postCount = list.metadata?.items?.filter(item => item.type === 'post').length || 0;
                    const restaurantCount = list.metadata?.items?.filter(item => item.type === 'restaurant').length || 0;
                    
                    return (
                      <div 
                        key={list.id}
                        className="list-card"
                        onClick={() => handleListClick(list.id)}
                      >
                        <div className="list-card-header">
                          <h3>{list.name}</h3>
                          <div className="list-actions">
                            <button 
                              className={`visibility-button ${list.visibility}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleVisibility(list.id, list.visibility);
                              }}
                              title={`Make list ${list.visibility === 'private' ? 'public' : 'private'}`}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                {list.visibility === 'private' ? (
                                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                ) : (
                                  <path fillRule="evenodd" d="M10 12a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                )}
                              </svg>
                            </button>
                            <button 
                              className="delete-list-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteList(list.id);
                              }}
                              title="Delete list"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div className="list-stats">
                          <span>{postCount} posts</span>
                          <span>{restaurantCount} restaurants</span>
                          <span className={`visibility-badge ${list.visibility}`}>
                            {list.visibility === 'private' ? 'Private' : 'Public'}
                          </span>
                        </div>
                        <div className="list-date">
                          Created {new Date(list.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : isLoadingCurrentTab ? (
            <p>Loading...</p>
          ) : postsToDisplay.length === 0 ? (
            <div className="no-posts">
              <p>
                {activeTab === 'myPosts'
                  ? "You haven't created any posts yet."
                  : "You haven't Yummed any posts yet."}
              </p>
              {activeTab === 'myPosts' && (
                <a href="/create-post" className="create-post-link">
                  Create your first post
                </a>
              )}
            </div>
          ) : (
            <div className="posts-grid">
              {postsToDisplay.map((post) => (
                activeTab === 'myPosts' ? (
                  <ProfilePostCard
                    key={post.id}
                    post={post}
                    isYummedPost={false}
                    onEdit={handleEditPost}
                    onDelete={handleDeletePost}
                  />
                ) : (
                  <PostCard
                    key={post.id}
                    post={post as PostData}
                    onCardClick={() => handlePostClick(post as PostData)}
                  />
                )
              ))}
            </div>
          )}
        </div>

        <div className="profile-actions">
          <button onClick={logout} className="action-button logout-button">Logout</button>
          <button
            onClick={handleDeleteAccountClick}
            className="action-button delete-account-button"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete Account'}
          </button>
          {deleteError && <p className="error-message">{deleteError}</p>}
        </div>

        {selectedPost && (
          <PostModal
            post={selectedPost}
            onClose={handleCloseModal}
          />
        )}

        <ReauthModal
          isOpen={showReauthModal}
          onClose={() => {
            setShowReauthModal(false);
            setDeleteError(null);
          }}
          onSuccess={performAccountDeletion}
          providerId={providerId}
        />

        <FollowListModal
          isOpen={isFollowModalOpen}
          onClose={closeFollowListModal}
          title={followListTitle}
          users={followListUsers ? followListUsers.map(convertToListUser) : null}
          isLoading={loading.followList}
          error={followListError}
        />
      </div>
    </div>
  );
} 